import { formatDate, MOMENT_FORMATS } from '../../utils/dateFormat';
import { normalizeEventTypeKey } from './eventTypes';
import { getDishImageUrl, getEventCoverImageFromApi } from './eventImageUtils';
import { formatEventTimeRange } from './ManageEvent/eventTimeUtils';
import { getSeatsStatusLabel } from './eventDetailsData';
import { resolveAvailableSeats } from './mapApiEventToListItem';
import { getRefundPolicyLabel } from './ManageEvent/partnerEventSettings';
import { normalizeMultilineText } from './ManageEvent/eventFormConstants';
import { isOnlineEventFormat, normalizeEventFormat } from './ManageEvent/eventFormatConstants';
import { normalizeTopicsFromApi } from './ManageEvent/eventTopicsConstants';
import { normalizeAttendeeEligibility } from '../../shared/registrationSettings';
import { getEventModerationRecords } from './eventModerationRecords';

const COURSE_API_LABELS = {
  starter: 'Starter',
  main: 'Main Course',
  desert: 'Dessert',
};

const formatPrice = (price, currency = 'AED') => {
  if (price == null || price === '') return '—';

  const currencyCode = String(currency || 'AED')
    .trim()
    .toUpperCase();

  if (typeof price === 'number') {
    return `${price} ${currencyCode}`;
  }

  const trimmed = String(price).trim();
  if (new RegExp(currencyCode, 'i').test(trimmed)) {
    return trimmed;
  }

  return `${trimmed} ${currencyCode}`;
};

const formatEventDate = (eventDate) =>
  formatDate(eventDate, MOMENT_FORMATS.DATE) || (eventDate ? String(eventDate) : '—');

const mapMenuItemsToSetMenu = (menuItems = {}) => {
  const result = [];

  Object.entries(COURSE_API_LABELS).forEach(([apiKey, course]) => {
    const dishes = menuItems[apiKey] ?? [];
    dishes.forEach((dish, index) => {
      result.push({
        course,
        name: dish.dishName || dish.description || dish.name || '—',
        dishName: dish.dishName ?? '',
        description: normalizeMultilineText(dish.description ?? ''),
        imageUrl: getDishImageUrl(dish),
        imageSeed: dish.id ?? `${apiKey}-${index}`,
        showOrDivider: index > 0,
      });
    });
  });

  return result;
};

const formatBookedUserPaymentStatus = (user) => {
  if (user?.refundedAt) return 'Refunded';
  const status = String(user?.status ?? '').toLowerCase();
  if (status === 'paid' && user.bypassPaid) return 'Bypassed';
  if (status === 'paid') return 'Paid';
  if (status === 'refunded') return 'Refunded';
  if (status === 'pending_payment') return 'Pending';
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const formatBookedUserRsvpStatus = (user) => {
  const status = String(user?.rsvpStatus ?? '').toLowerCase();

  if (status === 'waitlist') {
    return 'Waitlist';
  }
  if (status === 'confirmed' || !status) {
    return 'Confirmed';
  }

  return status.charAt(0).toUpperCase() + status.slice(1);
};

/** Maps event details `bookedUsers` to guest list rows. */
export const mapBookedUsersToGuests = (bookedUsers) => {
  if (!Array.isArray(bookedUsers)) return [];

  return bookedUsers.map((user, index) => {
    const outOfPlatform = Boolean(user.outOfPlatform);

    const attendeeId = user.attendeeId ?? user.bookingId ?? user.userId ?? null;

    return {
      id: String(attendeeId ?? index),
      attendeeId,
      bookingId: user.bookingId ?? null,
      userId: user.userId ?? null,
      name: user.fullName?.trim() || '—',
      email: user.userEmail?.trim() || '—',
      phone: user.phoneNumber?.trim() || '—',
      profession: user.profession?.trim() || '',
      avatarUrl: user.avatarUrl ?? '',
      outOfPlatform,
      paymentStatus: outOfPlatform ? 'Out of Platform' : formatBookedUserPaymentStatus(user),
      rsvpStatus: outOfPlatform ? 'Confirmed' : formatBookedUserRsvpStatus(user),
      bypassPaid: user.bypassPaid ?? false,
      paidAt: user.paidAt ?? null,
      refundedAt: user.refundedAt ?? null,
    };
  });
};

/** Maps GET /admin/events/:id payload to EventDetails view model. */
export const mapApiEventToDetails = (event) => {
  const id = String(event.id ?? '');
  const seatsTotal = Number(event.totalSeats ?? 0);
  const bookedCount = Array.isArray(event.bookedUsers) ? event.bookedUsers.length : 0;
  const seatsAvailable = resolveAvailableSeats(event, seatsTotal, bookedCount);
  const seatsTaken = Math.max(0, seatsTotal - seatsAvailable);
  const currency = event.currency ?? 'AED';
  const eventFormat = normalizeEventFormat(event.eventFormat);
  const isOnline = isOnlineEventFormat(eventFormat);
  const onlineMeetingLink = String(event.onlineMeetingLink ?? '').trim();
  const venueName = event.venueName ?? '';
  const fullAddress = event.location ?? '';
  const hasPartnerSettings = Boolean(event.createdByPartnerId);
  const partner = event.partner ?? event.createdByPartner ?? event.createdByPartnerProfile ?? null;

  return {
    id,
    title: event.title ?? '',
    type: normalizeEventTypeKey(event.type),
    price: formatPrice(event.price, currency),
    currency,
    description: normalizeMultilineText(event.description ?? ''),
    wingmanDescription: normalizeMultilineText(event.wingmanDescription ?? ''),
    coverImageUrl: getEventCoverImageFromApi(event),
    totalSeats: seatsTotal,
    bookingDeadline: event.bookingDeadline ?? null,
    eventDate: event.eventDate ?? null,
    startTime: event.startTime ?? '',
    endTime: event.endTime ?? '',
    date: formatEventDate(event.eventDate),
    timeRange: formatEventTimeRange(event.startTime, event.endTime) || '—',
    eventFormat,
    onlineMeetingLink,
    venueName,
    address: isOnline ? '' : fullAddress,
    latitude: event.latitude ?? null,
    longitude: event.longitude ?? null,
    stripeProductId: event.stripeProductId ?? null,
    stripePriceId: event.stripePriceId ?? null,
    published: Boolean(event.published),
    status: event.status ?? null,
    createdByPartnerId: event.createdByPartnerId ?? null,
    createdByName: partner?.fullName ?? partner?.name ?? '',
    createdByAvatarUrl: partner?.avatarUrl ?? partner?.logoUrl ?? '',
    hasPartnerSettings,
    enableWaitlist: Boolean(event.enableWaitlist),
    inviteOnly: hasPartnerSettings ? Boolean(event.inviteOnly) : null,
    requireJoinApproval: Boolean(event.requireJoinApproval),
    attendeeEligibility: normalizeAttendeeEligibility(event.attendeeEligibility),
    memberMatching: Boolean(event.memberMatching),
    refundPolicy: hasPartnerSettings ? (event.refundPolicy ?? null) : null,
    refundPolicyLabel: hasPartnerSettings ? getRefundPolicyLabel(event.refundPolicy) : null,
    topics: normalizeTopicsFromApi(event.topics),
    actualAttendance: event.actualAttendance ?? null,
    walkIns: event.walkIns ?? null,
    noShows: event.noShows ?? null,
    operationalNotes: event.operationalNotes ?? null,
    isFinalized: Boolean(event.isFinalized),
    createdAt: event.createdAt ?? null,
    updatedAt: event.updatedAt ?? null,
    deletedAt: event.deletedAt ?? null,
    menuItems: event.menuItems ?? { starter: [], main: [], desert: [] },
    setMenu: mapMenuItemsToSetMenu(event.menuItems ?? { starter: [], main: [], desert: [] }),
    availableSeats: seatsAvailable,
    seatsAvailable,
    seatsTaken,
    seatsStatusLabel: getSeatsStatusLabel(seatsAvailable, seatsTotal, seatsTaken),
    bookedUsers: Array.isArray(event.bookedUsers) ? event.bookedUsers : [],
    guests: mapBookedUsersToGuests(event.bookedUsers),
    viewedCount: Number(event.viewedCount ?? 0),
    moderationRecords: getEventModerationRecords(event),
  };
};
